import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { GoogleMap, LoadScript, Marker, DirectionsRenderer } from '@react-google-maps/api';
//icons
import { FaHourglassHalf } from 'react-icons/fa';
//constants
import { MAP_KEY } from 'constants';
//components
import Button from 'components/backoffice/buttonCustom';

const LibraryMap = (props) => {
  const { t } = useTranslation(['backoffice']);
  const [userLocation, setUserLocation] = useState(null);
  const [directions, setDirections] = useState(null);
  const [travelTime, setTravelTime] = useState(null);
  const [travelMode, setTravelMode] = useState(props.travelMode || 'SELECT');
  const [isLoaded, setIsLoaded] = useState(false);
  const [travelMessage, setTravelMessage] = useState('');
  const [geoError, setGeoError] = useState(null);
  const { userLocation: passedUserLocation, travelMode: passedTravelMode } = props;

  const mapOptions = {
    zoomControl: true,
    mapTypeControl: true,
    streetViewControl: true,
    fullscreenControl: true,
  };

  const mapContainerStyle = {
    width: '100%',
    height: '100vh',
  };

  const onLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  const getDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const calculateRoute = (destination, mode = travelMode) => {
    if (!userLocation || !destination || isNaN(destination.lat) || isNaN(destination.lng)) {
      return;
    }
    const distance = getDistance(userLocation.lat, userLocation.lng, destination.lat, destination.lng);
    if (distance > 5000) {
      setTravelMessage(t('yourTripDistance'));
      setDirections(null);
      setTravelTime(null);
      return;
    }
    setTravelMessage('');
    if (window.google) {
      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin: userLocation,
          destination: { lat: parseFloat(destination.lat), lng: parseFloat(destination.lng) },
          travelMode: mode,
        },
        (result, status) => {
          if (status === 'OK') {
            setDirections(result);
            setTravelTime(result.routes[0].legs[0].duration.text);
          } else if (status === 'ZERO_RESULTS') {
            setTravelMessage(t('noRoutesTransport'));
            setDirections(null);
            setTravelTime(null);
          } else {
            console.error('Error fetching directions:', status);
          }
        },
      );
    } else {
      console.error('Google Maps API is not loaded.');
    }
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({
            lat: latitude,
            lng: longitude,
          });
          setGeoError(null);
        },
        (error) => {
          setGeoError(error.message);
          console.error('Error al obtener la ubicación:', error);
        },
      );
    } else {
      setGeoError('La geolocalización no está soportada en este navegador.');
      console.error('La geolocalización no está soportada en este navegador.');
    }
  }, []);

  const retryGeolocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
          setGeoError(null);
        },
        (error) => {
          setGeoError(error.message);
        },
      );
    }
  };

  const travelModeOptions = [
    { value: 'DRIVING', label: t('car') },
    { value: 'WALKING', label: t('walking') },
    { value: 'BICYCLING', label: t('bicycle') },
    { value: 'TRANSIT', label: t('publicTransport') },
  ];

  const handleSelectChange = (selectedOption) => {
    const newMode = selectedOption?.value || 'SELECT';
    setTravelMode(newMode);
    if (newMode !== 'SELECT') {
      calculateRoute(passedUserLocation, newMode);
    }
  };

  const getMarkerIcon = (type) => {
    if (!isLoaded || !window.google) return null;
    return {
      url:
        type === 'user'
          ? 'https://maps.google.com/mapfiles/kml/paddle/blu-circle.png'
          : 'https://maps.google.com/mapfiles/kml/paddle/orange-circle.png',
      scaledSize: new window.google.maps.Size(50, 50),
    };
  };

  return (
    <div className="overflow-x-hidden">
      <Row className="p-4 visit-us">
        <Col md={12} xs={12} className="d-flex align-items-center justify-content-center mb-4">
          <h1 className="text-center text-md-start">{t('findYourVisit')}</h1>
        </Col>
        <Col md={12} xs={12} className="d-flex align-items-center justify-content-center mb-4">
          {geoError && (
            <Col md={12} className="text-center mb-3">
              <p className="text-danger">
                {t('locationDisabled')}
              </p>
              <Button className="btn-location" onClick={retryGeolocation}>
                {t('activateLocation')}
              </Button>
            </Col>
          )}
        </Col>
        <Col md={6} xs={12} className="d-flex align-items-center justify-content-center mb-4 mb-md-2">
          <Select
            placeholder={t('selectTravelMethod')}
            className="w-75"
            options={travelModeOptions}
            value={travelModeOptions.find((option) => option.value === travelMode)}
            onChange={handleSelectChange}
          />
        </Col>
        <Col md={6} xs={12} className="d-flex align-items-center justify-content-center justify-content-md-start mb-2">
          {travelMessage ? (
            <p className="mb-0 text-center text-md-start">{travelMessage}</p>
          ) : (
            <p className="mb-0 text-center text-md-start d-flex align-items-center">
              <FaHourglassHalf className="me-2 text-primary" />
              {t('estimatedTime')} {travelTime || t('selectTravelMethod')}
            </p>
          )}
        </Col>
      </Row>
      <LoadScript googleMapsApiKey={MAP_KEY} onLoad={onLoad} libraries={['places', 'directions']}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={userLocation || { lat: 0, lng: 0 }}
          zoom={3}
          options={mapOptions}
        >
          {isLoaded && userLocation && (
            <Marker position={userLocation} title="Tu ubicación" icon={getMarkerIcon('user')} />
          )}
          {isLoaded && passedUserLocation && (
            <Marker
              position={{ lat: parseFloat(passedUserLocation.lat), lng: parseFloat(passedUserLocation.lng) }}
              title="Ubicación de la librería"
              icon={getMarkerIcon('library')}
              onClick={() => calculateRoute(passedUserLocation)}
            />
          )}
          {directions && <DirectionsRenderer directions={directions} options={{ suppressMarkers: true }} />}
        </GoogleMap>
      </LoadScript>
    </div>
  );
};

export default LibraryMap;